import { Fragment, useMemo, useState } from "react";

import { DateTime } from "luxon";
import {
  ArrowsRightLeftIcon,
  ArrowTopRightOnSquareIcon,
  CheckIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";

import PartImage from "../stock/PartImage";
import UpdateTaxRate from "./UpdateTaxRate";
import useBaskets from "../../data/useBaskets";
import classNames from "../../utils/classNames";
import ClickToCopy from "../shared/ClickToCopy";
import { IBasketItem } from "../../models/Basket";
import { FormatType } from "../../models/EbayOffer";
import openInNewTab from "../../utils/openInNewTab";
import PriceFormat from "../../utils/priceFormat";
import BasketPriceEditor from "./BasketPriceEditor";
import VehicleCompareModal from "../shared/VehicleCompareModal";
import PartGradeBadge from "../shared/PartGradeBadge";
import PartNotesIndicator from "../shared/PartNotesIndicator";
import { IPart } from "../../models/Part";
import { IShippingPolicy } from "../../models/ShippingPolicy";
import Tooltip from "../shared/Tooltip";

interface BasketTableProps {
  data: IBasketItem[];
  customerVrm?: string;
  onCustomerVrmChange?: (vrm: string) => void;
  addTaxToTotal?: boolean;
  rowDiscounts?: Record<string, number>;
  onRowDiscountChange?: (id: string, pct: number) => void;
  shippingPolicies?: IShippingPolicy[];
  rowShippingPolicyIds?: Record<string, string>;
  onRowShippingChange?: (id: string, policyId: string) => void;
  noInvoiceItemIds?: string[];
  onNoInvoiceToggle?: (id: string) => void;
}

export default function BasketTable({
  data,
  customerVrm,
  onCustomerVrmChange,
  addTaxToTotal = false,
  rowDiscounts = {},
  onRowDiscountChange,
  shippingPolicies = [],
  rowShippingPolicyIds = {},
  onRowShippingChange,
  noInvoiceItemIds = [],
  onNoInvoiceToggle,
}: BasketTableProps) {
  const { removeItem } = useBaskets("BasketTable");
  const [sorting, setSorting] = useState<SortingState>([]);
  const [partChecks, setPartChecks] = useState<Set<string>>(new Set());
  const [placementChecks, setPlacementChecks] = useState<Set<string>>(new Set());
  const [imageChecks, setImageChecks] = useState<Set<string>>(new Set());
  const [customerMpns, setCustomerMpns] = useState<Record<string, string>>({});

  const togglePartCheck = (id: string) => toggleInSet(setPartChecks, id);
  const togglePlacementCheck = (id: string) => toggleInSet(setPlacementChecks, id);
  const toggleImageCheck = (id: string) => toggleInSet(setImageChecks, id);

  const noInvoiceSet = useMemo(
    () => new Set(noInvoiceItemIds),
    [noInvoiceItemIds]
  );

  const setCustomerMpn = (id: string, value: string) => {
    setCustomerMpns((prev) => ({ ...prev, [id]: value }));
  };

  const colHelper = createColumnHelper<IBasketItem>();

  const cols = useMemo(
    () => [
      colHelper.display({
        id: "images",
        cell: (info) => (
          <PartImage part={info.row.original.part} width="min-w-10 max-w-32" />
        ),
      }),
      colHelper.accessor("part.category.name", {
        id: "partInformation",
        header: "Part Information",
        sortingFn: "auto",
        meta: {
          thClassName: "w-40 min-w-[10rem] max-w-[12rem]",
          tdClassName: "w-40 min-w-[10rem] max-w-[12rem]",
        },
        cell: (info) => <PartInformationCell part={info.row.original.part} />,
      }),
      colHelper.accessor("part.vehicle.combinedMake", {
        id: "donorVehicle",
        header: "Donor Vehicle",
        sortingFn: "auto",
        cell: (info) => (
          <BasketVehicleCell
            basketItem={info.row.original}
            customerVrm={customerVrm}
            onCustomerVrmChange={onCustomerVrmChange}
          />
        ),
      }),
      colHelper.accessor("part.partNumber", {
        header: "Part No.",
        sortingFn: "auto",
        cell: (info) => {
          const id = info.row.original.id;
          const partNumber = info.getValue();
          const customerMpn = customerMpns[id] ?? "";
          const partTrim = (partNumber ?? "").trim().toLowerCase();
          const custTrim = customerMpn.trim().toLowerCase();
          const matches =
            !!partTrim && !!custTrim && partTrim === custTrim;
          const differs = !!partTrim && !!custTrim && !matches;

          return (
            <div className="flex min-w-[10rem] flex-col gap-1">
              <ClickToCopy text={partNumber} />
              <label className="flex items-center gap-1.5 text-[11px] text-slate-600">
                <span className="w-12 flex-none font-semibold uppercase tracking-wide text-slate-500">
                  Cust
                </span>
                <input
                  type="text"
                  value={customerMpn}
                  onChange={(e) => setCustomerMpn(id, e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  placeholder="Customer MPN"
                  className={classNames(
                    "h-7 w-full rounded border px-1.5 text-xs uppercase tracking-wide placeholder:normal-case placeholder:tracking-normal focus:outline-none focus:ring-1",
                    matches
                      ? "border-emerald-300 bg-emerald-50 text-emerald-800 focus:border-emerald-400 focus:ring-emerald-400"
                      : differs
                      ? "border-rose-300 bg-rose-50 text-rose-800 focus:border-rose-400 focus:ring-rose-400"
                      : "border-slate-300 bg-white text-slate-800 focus:border-primary-400 focus:ring-primary-400"
                  )}
                />
                {matches && (
                  <CheckIcon
                    className="h-3.5 w-3.5 flex-none text-emerald-600"
                    aria-hidden="true"
                  />
                )}
              </label>
            </div>
          );
        },
      }),
      colHelper.display({
        id: "checks",
        header: "Checks",
        cell: (info) => {
          const id = info.row.original.id;
          return (
            <div className="flex flex-col gap-1">
              <CheckToggle
                label="Parts"
                checked={partChecks.has(id)}
                onToggle={() => togglePartCheck(id)}
              />
              <CheckToggle
                label="Placement"
                checked={placementChecks.has(id)}
                onToggle={() => togglePlacementCheck(id)}
              />
              <CheckToggle
                label="Images"
                checked={imageChecks.has(id)}
                onToggle={() => toggleImageCheck(id)}
              />
              <CheckToggle
                label="No Invoice"
                checked={noInvoiceSet.has(id)}
                onToggle={() => onNoInvoiceToggle?.(id)}
              />
            </div>
          );
        },
      }),
      colHelper.accessor("part.offers", {
        id: "price",
        header: "Price",
        enableSorting: false,
        cell: (info) => <BasketPriceEditor basketItem={info.row.original} />,
      }),
      colHelper.display({
        id: "discountPct",
        header: "Discount %",
        cell: (info) => (
          <RowDiscountInput
            basketItem={info.row.original}
            value={rowDiscounts[info.row.original.id] ?? 0}
            onChange={(pct) =>
              onRowDiscountChange?.(info.row.original.id, pct)
            }
          />
        ),
      }),
      colHelper.accessor("taxRate", {
        id: "taxRate",
        header: "VAT %",
        sortingFn: "auto",
        meta: {
          thClassName: "w-24 min-w-[6rem] max-w-[7rem]",
          tdClassName: "w-24 min-w-[6rem] max-w-[7rem]",
        },
        cell: (info) => <UpdateTaxRate basketItem={info.row.original} />,
      }),
      colHelper.display({
        id: "vatAmount",
        header: "VAT",
        meta: {
          thClassName: "w-20 min-w-[5rem] max-w-[6rem]",
          tdClassName: "w-20 min-w-[5rem] max-w-[6rem]",
        },
        cell: (info) => (
          <RowVatCell
            basketItem={info.row.original}
            addTaxToTotal={addTaxToTotal}
            rowDiscountPct={rowDiscounts[info.row.original.id] ?? 0}
          />
        ),
      }),
      colHelper.display({
        id: "shipping",
        header: "Shipping",
        meta: {
          thClassName: "w-48 min-w-[12rem] max-w-[14rem]",
          tdClassName: "w-48 min-w-[12rem] max-w-[14rem]",
        },
        cell: (info) => (
          <RowShippingSelect
            basketItem={info.row.original}
            policies={shippingPolicies}
            value={rowShippingPolicyIds[info.row.original.id] ?? ""}
            onChange={(policyId) =>
              onRowShippingChange?.(info.row.original.id, policyId)
            }
          />
        ),
      }),
      colHelper.display({
        id: "actions",
        header: "",
        cell: (info) => (
          <div className="flex items-center justify-end gap-1">
            <Tooltip content="View part">
              <button
                type="button"
                onClick={() =>
                  openInNewTab(`stock/${info.row.original.part.id}`)
                }
                aria-label="View part"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-600 ring-1 ring-slate-300 transition-colors hover:bg-slate-50 hover:text-primary-600 hover:ring-primary-400"
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            </Tooltip>
            <Tooltip content="Remove from basket">
              <button
                type="button"
                onClick={() => removeItem.mutate(info.row.original.part.id)}
                aria-label="Remove from basket"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full text-rose-600 ring-1 ring-rose-200 transition-colors hover:bg-rose-50 hover:ring-rose-400"
              >
                <TrashIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            </Tooltip>
          </div>
        ),
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      partChecks,
      placementChecks,
      imageChecks,
      noInvoiceSet,
      onNoInvoiceToggle,
      customerVrm,
      onCustomerVrmChange,
      customerMpns,
      rowDiscounts,
      addTaxToTotal,
      shippingPolicies,
      rowShippingPolicyIds,
    ]
  );

  const table = useReactTable({
    data: data,
    columns: cols,
    state: {
      sorting: sorting,
    },
    enableRowSelection: true,
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="mt-4 bg-white overflow-auto">
      <table className="min-w-full overflow-scroll divide-y border divide-gray-300">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="bg-primary-50 text-gray-800">
              {headerGroup.headers.map((header) => {
                const meta = header.column.columnDef.meta as
                  | { thClassName?: string }
                  | undefined;
                return (
                <th
                  key={header.id}
                  scope="col"
                  className={classNames(
                    "py-3.5 px-3 text-sm font-semibold lg:table-cell text-left",
                    meta?.thClassName ?? ""
                  )}
                >
                  {header.isPlaceholder ? null : (
                    <div
                      {...{
                        className: header.column.getCanSort()
                          ? "cursor-pointer select-none"
                          : "",
                        onClick: header.column.getToggleSortingHandler(),
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {{
                        asc: " ▲",
                        desc: " ▼",
                      }[header.column.getIsSorted() as string] ?? null}
                    </div>
                  )}
                </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row, i) => {
            const rowCells = row.getVisibleCells();
            const id = row.original.id;
            const fullyChecked =
              partChecks.has(id) &&
              placementChecks.has(id) &&
              imageChecks.has(id);
            const isNoInvoice = noInvoiceSet.has(id);
            return (
              <Fragment key={i}>
                <tr
                  key={row.id}
                  className={classNames(
                    "hover:bg-gray-100",
                    isNoInvoice
                      ? "bg-amber-50/60"
                      : fullyChecked
                      ? "bg-emerald-50/40"
                      : ""
                  )}
                >
                  {rowCells.map((cell) => {
                    const meta = cell.column.columnDef.meta as
                      | { tdClassName?: string }
                      | undefined;
                    return (
                      <td
                        key={cell.id}
                        className={classNames(
                          "px-3 py-3 border text-sm align-top",
                          meta?.tdClassName ?? ""
                        )}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PartInformationCell({ part }: { part: IPart }) {
  return (
    <div className="flex min-w-[10rem] max-w-[12rem] flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-slate-800">
          {part.category?.name ?? "—"}
        </span>
        <PartGradeBadge part={part} />
        <PartNotesIndicator part={part} />
      </div>
      {part.tagNumber && (
        <div className="text-xs text-slate-600">
          <span className="mr-1 font-semibold uppercase tracking-wide text-slate-400">
            Tag
          </span>
          <span>{part.tagNumber}</span>
        </div>
      )}
      <div className="flex items-center gap-1.5 text-xs text-slate-500">
        <span className="font-semibold uppercase tracking-wide text-slate-400">
          Age
        </span>
        <PartAgeCell part={part} />
      </div>
    </div>
  );
}

function PartAgeCell({ part }: { part: IPart }) {
  const sourceIso = part.removedOn ?? part.created ?? null;
  if (!sourceIso) {
    return <span className="text-xs text-slate-400">—</span>;
  }

  const source = DateTime.fromISO(sourceIso);
  if (!source.isValid) {
    return <span className="text-xs text-slate-400">—</span>;
  }

  const days = Math.max(0, Math.floor(DateTime.now().diff(source, "days").days));
  const fromRemoved = !!part.removedOn;

  return (
    <Tooltip
      content={
        fromRemoved
          ? `Days since removed from donor (${source.toFormat("dd LLL yyyy")})`
          : `Days since added (${source.toFormat("dd LLL yyyy")})`
      }
    >
      <span
        className={classNames(
          "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
          days >= 90
            ? "bg-rose-100 text-rose-700 ring-1 ring-rose-200"
            : days >= 30
            ? "bg-amber-100 text-amber-700 ring-1 ring-amber-200"
            : "bg-slate-100 text-slate-700 ring-1 ring-slate-200"
        )}
      >
        {days} {days === 1 ? "day" : "days"}
      </span>
    </Tooltip>
  );
}

function BasketVehicleCell({
  basketItem,
  customerVrm,
  onCustomerVrmChange,
}: {
  basketItem: IBasketItem;
  customerVrm?: string;
  onCustomerVrmChange?: (vrm: string) => void;
}) {
  const [compareOpen, setCompareOpen] = useState(false);
  const vehicle = basketItem.part.vehicle;
  const trimmedVrm = (customerVrm ?? "").trim();

  return (
    <div className="flex flex-col gap-1">
      <ClickToCopy text={vehicle?.vrm} />
      <span>{vehicle?.combinedMake ?? ""}</span>
      <span>{vehicle?.combinedModel ?? ""}</span>
      <ClickToCopy text={vehicle?.combinedVIN} />

      {vehicle && (
        <Tooltip
          content={
            trimmedVrm
              ? `Compare donor vs ${trimmedVrm.toUpperCase()}`
              : "Compare donor against a customer reg"
          }
        >
          <button
            type="button"
            onClick={() => setCompareOpen(true)}
            className="mt-1 inline-flex items-center gap-1 self-start rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700 ring-1 ring-primary-200 transition-colors hover:bg-primary-100"
          >
            <ArrowsRightLeftIcon className="h-3.5 w-3.5" aria-hidden="true" />
            Compare
          </button>
        </Tooltip>
      )}

      {vehicle && (
        <VehicleCompareModal
          open={compareOpen}
          setOpen={setCompareOpen}
          donor={vehicle}
          initialVrm={trimmedVrm}
          onVrmChange={onCustomerVrmChange}
        />
      )}
    </div>
  );
}

function getRawItemPrice(item: IBasketItem): number {
  if (item.basketPrice !== undefined && item.basketPrice !== null) {
    return Number(item.basketPrice) || 0;
  }
  if (item.part.offers && item.part.offers.length > 0) {
    const offer = item.part.offers[0];
    if (offer.format === FormatType.FixedPrice) {
      return parseFloat(offer.buyItNowPrice?.toString() || "0") || 0;
    }
    return parseFloat(offer.auctionStartPrice?.toString() || "0") || 0;
  }
  return parseFloat(item.part.enteredPrice?.toString() || "0") || 0;
}

function RowDiscountInput({
  basketItem,
  value,
  onChange,
}: {
  basketItem: IBasketItem;
  value: number;
  onChange: (pct: number) => void;
}) {
  const [local, setLocal] = useState<string>(value ? value.toString() : "0");

  const commit = () => {
    const parsed = parseFloat(local);
    const clamped = Math.min(100, Math.max(0, isNaN(parsed) ? 0 : parsed));
    onChange(clamped);
    setLocal(clamped.toString());
  };

  return (
    <div
      className="flex items-center gap-1"
      onClick={(e) => e.stopPropagation()}
    >
      <input
        key={basketItem.id}
        type="number"
        step={0.01}
        min={0}
        max={100}
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            (e.target as HTMLInputElement).blur();
          } else if (e.key === "Escape") {
            setLocal(value ? value.toString() : "0");
          }
        }}
        className="h-8 w-16 rounded border border-slate-300 p-1 text-sm focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
      />
      <span className="text-xs text-slate-500">%</span>
    </div>
  );
}

function RowShippingSelect({
  basketItem,
  policies,
  value,
  onChange,
}: {
  basketItem: IBasketItem;
  policies: IShippingPolicy[];
  value: string;
  onChange: (policyId: string) => void;
}) {
  const selected = policies.find((p) => p.id === value);

  return (
    <div
      className="flex flex-col gap-1"
      onClick={(e) => e.stopPropagation()}
    >
      <select
        key={basketItem.id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={classNames(
          "h-8 w-full rounded border px-1.5 text-xs focus:outline-none focus:ring-1",
          selected
            ? "border-slate-300 bg-white text-slate-800 focus:border-primary-400 focus:ring-primary-400"
            : "border-rose-300 bg-rose-50 text-rose-700 focus:border-rose-400 focus:ring-rose-400"
        )}
      >
        <option value="">Select shipping…</option>
        {policies.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
      <span className="text-[11px] font-medium text-slate-600">
        {selected ? PriceFormat(selected.price) : "—"}
      </span>
    </div>
  );
}

function RowVatCell({
  basketItem,
  addTaxToTotal,
  rowDiscountPct,
}: {
  basketItem: IBasketItem;
  addTaxToTotal: boolean;
  rowDiscountPct: number;
}) {
  const rawPrice = getRawItemPrice(basketItem);
  const discountedPrice =
    rawPrice * (1 - Math.min(100, Math.max(0, rowDiscountPct)) / 100);
  const taxRate =
    Math.min(Math.max(Number(basketItem.taxRate) || 0, 0), 100) / 100;
  // Match parent totals: tax = inputPrice * rate in both modes (the meaning of
  // inputPrice as net vs gross differs but the line-level VAT figure is the same).
  const vatAmount = discountedPrice * taxRate;

  return (
    <Tooltip
      content={
        (addTaxToTotal ? "Added to net" : "Included in price") +
        (rowDiscountPct > 0
          ? ` · base ${PriceFormat(discountedPrice)} (−${rowDiscountPct}%)`
          : "")
      }
    >
      <span className="text-sm font-medium text-slate-800">
        {PriceFormat(vatAmount)}
      </span>
    </Tooltip>
  );
}

function toggleInSet(
  setter: React.Dispatch<React.SetStateAction<Set<string>>>,
  id: string
) {
  setter((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
}

function CheckToggle({
  label,
  checked,
  onToggle,
}: {
  label: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={checked}
      aria-label={`${checked ? "Unmark" : "Mark"} ${label.toLowerCase()} as checked`}
      className={classNames(
        "inline-flex h-5 items-center gap-1 rounded px-1.5 text-[11px] font-medium transition-colors",
        checked
          ? "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-300 hover:bg-emerald-200"
          : "bg-slate-100 text-slate-600 ring-1 ring-slate-300 hover:bg-slate-200"
      )}
    >
      <span
        className={classNames(
          "inline-flex h-3 w-3 items-center justify-center rounded-sm ring-1",
          checked
            ? "bg-emerald-600 text-white ring-emerald-600"
            : "bg-white ring-slate-300"
        )}
        aria-hidden="true"
      >
        {checked && <CheckIcon className="h-2.5 w-2.5" />}
      </span>
      {label}
    </button>
  );
}
